<template>
   <div data-cy="profile-role-block">
      <SelectChoiceGroup
         :isDisabled="!!props?.userBlockAnswer?.data?.role"
         :choices="choices"
         group="single-choice"
         @choice-updated="updateChoices"
      />
   </div>
   <InvalidInputsList :errorList="errorList" :class="{ hidden: !showBlockErrors }" />
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { useUserStore } from "@/stores/userStore";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";

export interface ProfileRoleAnswerData {
   role: "participant" | "caregiver";
}

const { t: $t } = useI18n();
const userStore = useUserStore();
const emit = defineEmits(["update-answer", "update-valid"]);

interface Choice {
   id: "participant" | "caregiver";
   label: string;
   isSelected: boolean;
}

export interface ProfileRoleBlock extends FlowselBlock {
   data: {
      choices: Choice[];
   };
}

// props
// -----------------------------------------
interface Props {
   block: ProfileRoleBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

const props = defineProps<Props>();

// component data
// -----------------------------------------
const state = reactive({
   role: props.userBlockAnswer?.data?.role,
});

// computed properties
// -----------------------------------------
const choices = computed(() => {
   return props.block.data.choices.map((choice) => {
      return {
         ...choice,
         isSelected: state.role === choice.id,
      };
   });
});

const errorList = computed(() => {
   if (!state.role)
      return [
         {
            inputId: props.block.id,
            validator: "required",
            message: $t("validation.choice.required"),
         },
      ];
   return [];
});

// component methods
// -----------------------------------------
const updateChoices = (userChoice) => {
   state.role = userChoice.id;
   processInputs();
};

const checkUserBlockAnswer = async () => {
   if (props.userBlockAnswer?.data?.role) {
      state.role = props.userBlockAnswer.data.role;
   } else {
      try {
         await userStore.setUser();
         if (typeof userStore.user?.isRepresented === "boolean") {
            // default to participant if no role is set
            state.role = userStore.user?.isRepresented ? "caregiver" : "participant";
         } else {
            console.info("No profile data available for user");
         }
      } catch (e) {
         console.error("Failed to fetch user data", e);
      }
   }
   processInputs();
};

function processInputs() {
   if (!state.role) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid. This may be because there is a valid answer, or no answer
      // when this question is not required. For no answer, a different data format is required by the BE.
      const answerToSubmit = state.role ? { role: state.role } : null;
      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
}

checkUserBlockAnswer();
</script>
